import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import bezelUrl from '../assets/road/light_bezel.png';
import { Icon } from './Icon';
import {
  MAP_RING,
  PILL_GAP,
  PLATE_TUCK,
  countsLine,
  countsStacked,
  mapButtonLabel,
  pickFit,
  type MapButtonInfo,
  type PlateFit,
  type PlateWidths,
} from './mapButton';

const DISPLAY = '"Lilita One", "Fredoka", system-ui, sans-serif';
const BODY = 'Fredoka, system-ui, sans-serif';
/** The words' sizes at each fit, CSS px. */
const SIZE = { full: { t1: 16, t2: 11.5 }, small: { t1: 14, t2: 10.5 }, counts: 13, stacked: { t1: 13, t2: 11 } } as const;
/** The title's letter spacing, and a little slack for its stroke. */
const TRACK = 0.3;
const SLACK = 3;

let measureCanvas: HTMLCanvasElement | null = null;
function textWidth(text: string, font: string, track = 0): number {
  measureCanvas ??= document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  if (!ctx) return text.length * 9;
  ctx.font = font;
  return Math.ceil(ctx.measureText(text).width + track * text.length) + SLACK;
}

function wordWidths(info: MapButtonInfo): PlateWidths {
  const line = countsLine(info);
  const [worlds, games] = countsStacked(info);
  const at = (t1: number, t2: number) =>
    Math.max(textWidth(info.title, `${t1}px ${DISPLAY}`, TRACK), textWidth(line, `600 ${t2}px ${BODY}`));
  return {
    full: at(SIZE.full.t1, SIZE.full.t2),
    small: at(SIZE.small.t1, SIZE.small.t2),
    counts: textWidth(line, `${SIZE.counts}px ${DISPLAY}`, TRACK),
    stacked: Math.max(
      textWidth(worlds, `${SIZE.stacked.t1}px ${DISPLAY}`, TRACK),
      textWidth(games, `600 ${SIZE.stacked.t2}px ${BODY}`),
    ),
  };
}

/** The wallet pills along the top (the slot's, or the wheel lobby's own). */
function topPills(): Element[] {
  return [...document.querySelectorAll('[data-testid="balance-pill"]')].filter((p) => {
    const r = p.getBoundingClientRect();
    return r.width > 0 && r.top < 80;
  });
}

/** The leftmost wallet pill's left edge on screen (they are right-aligned), or the screen's right margin without one. */
function pillLeft(): number {
  const lefts = topPills().map((p) => p.getBoundingClientRect().left);
  return lefts.length > 0 ? Math.min(...lefts) : window.innerWidth - 18;
}

/**
 * The way back from a slot (and the wheel lobby) at the top left: the map's gold ring round a painted map, a red badge
 * with the games in this world the player has not opened, and a plate naming where it leads over the whole game's
 * size ("7 worlds · 49 games"), so a player sees how much is out there (producer 2026-11-03). The plate measures the
 * wallet pill beside it and steps down (smaller words, the counts alone, the ring alone) rather than ever meeting it.
 * The exit itself lives in {@code App.handleExitToMap}.
 */
export function BackToMapButton({
  onExit,
  info,
  disabled = false,
}: {
  onExit: () => void;
  info: MapButtonInfo;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [fit, setFit] = useState<PlateFit>('none');
  // Measured again only when what it says changes (the caller makes a new object each render).
  const { title: where, waiting, worlds, games } = info;
  const [pressed, setPressed] = useState(false);

  useLayoutEffect(() => {
    const button = ref.current;
    if (!button) return undefined;
    const said = { title: where, waiting, worlds, games };
    let words = wordWidths(said);
    const measure = () => {
      const plateLeft = button.getBoundingClientRect().left + MAP_RING - PLATE_TUCK;
      setFit(pickFit(pillLeft() - PILL_GAP - plateLeft, words));
    };
    measure();
    let live = true;
    // The display font arrives after the first paint: measure the words again in it.
    void document.fonts?.ready.then(() => {
      if (!live) return;
      words = wordWidths(said);
      measure();
    });
    // A pill grows with its balance; the wheel lobby's may come and go: watch the ones there, and look again each second.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    for (const pill of topPills()) ro?.observe(pill);
    const again = window.setInterval(measure, 1000);
    window.addEventListener('resize', measure);
    return () => {
      live = false;
      ro?.disconnect();
      window.clearInterval(again);
      window.removeEventListener('resize', measure);
    };
  }, [where, waiting, worlds, games]);

  const title: CSSProperties = {
    fontFamily: DISPLAY,
    color: '#ffe48a',
    letterSpacing: TRACK,
    WebkitTextStroke: '0.6px #1a0d00',
    paintOrder: 'stroke fill',
    whiteSpace: 'nowrap',
  };
  const line: CSSProperties = { fontFamily: BODY, fontWeight: 600, color: '#fff3d0', whiteSpace: 'nowrap', marginTop: 2 };

  return (
    <button
      ref={ref}
      type="button"
      data-testid="map-button"
      aria-label={mapButtonLabel(info, disabled)}
      onClick={disabled ? undefined : onExit}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      style={{
        position: 'absolute',
        // Below the Dynamic Island on iPhone Pro models (env(...) is 0 on the web).
        top: 'calc(env(safe-area-inset-top, 0px) + 2px)',
        left: 'calc(env(safe-area-inset-left, 0px) + 10px)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        padding: 0,
        border: 0,
        background: 'none',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? 'none' : undefined,
        transform: pressed ? 'scale(0.95)' : undefined,
        transition: 'transform 90ms ease-out',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{ position: 'relative', width: MAP_RING, height: MAP_RING, flex: 'none', zIndex: 2 }}>
        <span
          style={{
            position: 'absolute',
            inset: 5,
            borderRadius: '50%',
            background: 'radial-gradient(circle at 50% 35%, #2a3f6e, #121c38)',
          }}
        />
        <img src={bezelUrl} alt="" style={{ position: 'absolute', inset: 0, width: MAP_RING, height: MAP_RING }} />
        <Icon
          name="map"
          size={38}
          alt=""
          style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -52%)' }}
        />
        {info.waiting > 0 ? (
          <span
            data-testid="map-button-waiting"
            style={{
              position: 'absolute',
              right: -3,
              top: -2,
              minWidth: 18,
              height: 18,
              boxSizing: 'border-box',
              padding: '0 4px',
              borderRadius: 9,
              background: '#e8462e',
              border: '2px solid #fff3d0',
              color: '#fff',
              font: `700 11px/14px ${BODY}`,
              textAlign: 'center',
            }}
          >
            {info.waiting}
          </span>
        ) : null}
      </span>
      {fit !== 'none' ? (
        <span
          data-testid="map-button-plate"
          data-fit={fit}
          style={{
            marginLeft: -PLATE_TUCK,
            padding: fit === 'counts' ? '6px 13px 6px 20px' : '4px 13px 5px 20px',
            borderRadius: '0 12px 12px 0',
            background: 'linear-gradient(#3b2410, #241406)',
            border: '2px solid #d9a441',
            borderLeft: 'none',
            boxShadow: 'inset 0 1px 0 #ffffff22',
            lineHeight: 1.05,
            textAlign: 'left',
            zIndex: 1,
          }}
        >
          {fit === 'counts' ? (
            <span style={{ ...title, display: 'block', fontSize: SIZE.counts }}>{countsLine(info)}</span>
          ) : fit === 'stacked' ? (
            <>
              <span style={{ ...title, display: 'block', fontSize: SIZE.stacked.t1 }}>{countsStacked(info)[0]}</span>
              <span style={{ ...line, display: 'block', fontSize: SIZE.stacked.t2 }}>{countsStacked(info)[1]}</span>
            </>
          ) : (
            <>
              <span style={{ ...title, display: 'block', fontSize: SIZE[fit].t1 }}>{info.title}</span>
              <span style={{ ...line, display: 'block', fontSize: SIZE[fit].t2 }}>
                <b style={{ color: '#ffd25a', fontWeight: 700 }}>{info.worlds} worlds</b> · {info.games} games
              </span>
            </>
          )}
        </span>
      ) : null}
    </button>
  );
}
